import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import DropdownMenu from "@ats/src/components/shared/DropdownMenu";
import SmallButton from "@ats/src/components/shared/SmallButton";
import FormRadio from "@ats/src/components/forms/FormRadio";
import { FeatureFlipper } from "@ats/src/components/shared/FeatureFlipper";

import { jobApplicationSortOptions, JobApplicationSortOption } from "@ats/src/lib/newLookups";

interface JobApplicationSortMenuProps {
  jobApplicationSort: JobApplicationSortOption;
  handleSortChange: (jobApplicationSortOption: JobApplicationSortOption) => void;
}

const JobApplicationSortMenu: React.FunctionComponent<JobApplicationSortMenuProps> = ({
  jobApplicationSort,
  handleSortChange,
}) => {
  const renderSortRow = (key: string) => {
    const jobApplicationSortOption = jobApplicationSortOptions.find((option) => option.key === key);

    return (
      <Styled.Row key={jobApplicationSortOption.key}>
        <FormRadio
          id={`job-application-sort-${jobApplicationSortOption.key}`}
          name="job-application-sort"
          value={jobApplicationSortOption.key}
          checked={jobApplicationSortOption.key === jobApplicationSort.key}
          onChange={() => handleSortChange(jobApplicationSortOption)}
          label={jobApplicationSortOption.label}
        />
        {jobApplicationSortOption.key === "last_activity_desc" && (
          <Styled.DefaultTag>Default</Styled.DefaultTag>
        )}
      </Styled.Row>
    );
  };

  return (
    <Styled.Container>
      <DropdownMenu
        icon="arrow-up-down"
        label="Sort"
        hpos="right"
        badge={jobApplicationSort.key !== "last_activity_desc" ? <Styled.ActiveDot /> : null}
      >
        <Styled.Body role="radiogroup" aria-label="Sort by" onClick={(e) => e.stopPropagation()}>
          <Styled.Section>Sort by</Styled.Section>
          {renderSortRow("last_activity_desc")}
          {renderSortRow("last_activity_asc")}
          <Styled.Divider />
          {renderSortRow("created_at_desc")}
          {renderSortRow("created_at_asc")}
          <Styled.Divider />
          <FeatureFlipper feature="AI_APPLICANT_SUMMARY">
            <Styled.PlatoSection>
              {renderSortRow("fit_desc")}
              {renderSortRow("fit_asc")}
            </Styled.PlatoSection>
            <Styled.Divider />
          </FeatureFlipper>
          {renderSortRow("last_name_asc")}
          {renderSortRow("last_name_desc")}
          <Styled.Divider />
          {renderSortRow("first_name_asc")}
          {renderSortRow("first_name_desc")}
        </Styled.Body>

        <Styled.Footer>
          <SmallButton styleType="secondary">Close</SmallButton>
        </Styled.Footer>
      </DropdownMenu>
    </Styled.Container>
  );
};

export default JobApplicationSortMenu;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Container = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: JobApplicationSortMenu_Container;

    [data-testid="dropdown-menu-list"] {
      border-radius: 7px;
      padding-bottom: 5px;
      ${t.dark ? "" : `border-color: ${t.color.gray[200]};`}
    }
  `;
});

Styled.Body = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: JobApplicationSortMenu;
    display: flex;
    flex-direction: column;
    min-width: 220px;
  `;
});

Styled.Section = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: JobApplicationSortMenu_Section;
    padding: 10px 12px 6px;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${t.color.gray[400]};
  `;
});

Styled.Row = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: JobApplicationSortMenu_Row;
    display: flex;
    align-items: center;
    gap: 12px;
    white-space: nowrap;

    /* override dropdown svg size */
    svg {
      width: 14px !important;
      height: 14px !important;
      margin-left: 0 !important;
    }

    &:hover {
      background-color: ${t.dark ? t.color.gray[700] : t.color.gray[100]};
    }

    > div {
      flex: 1;
      height: auto;
    }

    > div > [type="radio"] + label[for] {
      height: auto;
      width: 100%;
      padding: 6px 12px 6px 39px;
      font-size: 12px;
      line-height: 18px;
      font-weight: 400 !important;
      border-radius: 0;

      &:hover,
      &:focus {
        background-color: transparent !important;
      }

      &::before {
        left: 12px;
      }

      &::after {
        left: 17px;
      }
    }

    &:hover > div > [type="radio"] + label[for] {
      color: ${t.dark ? t.color.gray[200] : t.color.black};
    }

    &:hover > div > [type="radio"]:not(:checked) + label[for]::before {
      border-color: ${t.dark ? t.color.gray[600] : t.color.gray[400]};
    }
  `;
});

Styled.PlatoSection = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: JobApplicationSortMenu_PlatoSection;
    position: relative;

    &::before {
      content: "";
      position: absolute;
      left: -${t.spacing[1]};
      top: -5px;
      bottom: -5px;
      width: 3px;
      background: linear-gradient(180deg, #fbd7ff, #ffdec1);
    }
  `;
});

Styled.Divider = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: JobApplicationSortMenu_Divider;
    margin: 5px -${t.spacing[1]};
    border-top: 1px solid ${t.dark ? t.color.gray[600] : t.color.gray[200]};
  `;
});

Styled.DefaultTag = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: JobApplicationSortMenu_DefaultTag;
    margin-left: auto;
    padding-right: 12px;
    font-size: 10px;
    color: ${t.color.gray[400]};
  `;
});

Styled.ActiveDot = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: JobApplicationSortMenu_ActiveDot;
    display: block;
    position: relative;
    top: 5px;
    left: -5px;
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: ${t.dark ? t.color.gray[200] : t.color.black};
  `;
});

Styled.Footer = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: JobApplicationSortMenu_Footer;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-left: -${t.spacing[1]};
    margin-right: -${t.spacing[1]};
    padding: 8px 8px 3px;
    border-top: 1px solid ${t.dark ? t.color.gray[600] : t.color.gray[200]};

    && > button {
      ${t.rounded.sm}
      height: 40px;
      padding: 2px 16px 0;
      font-size: 16px;
      width: auto;
      margin-bottom: 0;
      justify-content: center;
      font-weight: 400 !important;

      ${t.mq["md"]} {
        height: 29px;
        font-size: 13px;
        font-weight: 450 !important;
      }

      color: ${t.dark ? t.color.gray[200] : t.color.black};
      background-color: ${t.dark ? t.color.gray[700] : t.color.gray[200]};
      border: 1px solid ${t.color.gray[200]};

      &:hover,
      &:focus {
        color: ${t.dark ? t.color.white : t.color.black};
        background-color: ${t.dark ? t.color.gray[600] : t.color.gray[300]} !important;
      }
    }
  `;
});
